import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoColorPaletteOutline } from "react-icons/io5";
import { DarkModeSwitch } from "react-toggle-dark-mode";
import useTheme, { THEMES } from "../Hooks/useTheme";

const Switcher = () => {
  const [theme, setTheme] = useTheme();
  const [open, setOpen] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  const ref = useRef(null);
  const timerRef = useRef(null);

  const isDark = ["dark", "ocean", "forest", "purple"].includes(theme);

  const toggleDark = (checked) => setTheme(checked ? "dark" : "light");

  // Auto-cycle every 1 minute when enabled
  useEffect(() => {
    if (autoPlay) {
      timerRef.current = setInterval(() => {
        const keys = THEMES.map((t) => t.key);
        setTheme((prev) => {
          const next = (keys.indexOf(prev) + 1) % keys.length;
          return keys[next];
        });
      }, 60 * 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [autoPlay, setTheme]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="flex items-center gap-2" ref={ref}>

      {/* Dark mode toggle */}
      <DarkModeSwitch
        checked={isDark}
        onChange={toggleDark}
        className="ml-2"
        moonColor="#EA580C"
        sunColor="#EA580C"
        size={40}
      />

      {/* Palette picker */}
      <div className="relative">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center justify-center w-9 h-9 rounded-full border-2 transition-all duration-200 hover:scale-110"
          style={{ borderColor: "var(--accent)", color: "var(--accent)", background: "var(--bg-card)" }}
          title="Change theme"
        >
          <IoColorPaletteOutline size={18} />
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.18 }}
              className="absolute right-0 top-12 z-50 p-3 rounded-2xl shadow-2xl border flex flex-col gap-2 min-w-[170px]"
              style={{ background: "var(--bg-card)", borderColor: "var(--border)" }}
            >
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: "var(--text-muted)" }}>
                Theme
              </p>

              {/* Theme list */}
              {THEMES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => { setTheme(t.key); setOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-150 hover:scale-[1.02]"
                  style={{
                    background: theme === t.key ? "var(--accent-bg)" : "transparent",
                    border: `1.5px solid ${theme === t.key ? "var(--accent)" : "transparent"}`,
                  }}
                >
                  <span className="w-5 h-5 rounded-full flex-shrink-0 border-2" style={{ background: t.color, borderColor: t.border }} />
                  <span className="text-sm font-medium" style={{ color: theme === t.key ? "var(--accent)" : "var(--text)" }}>
                    {t.label}
                  </span>
                  {theme === t.key && <span className="ml-auto text-xs" style={{ color: "var(--accent)" }}>✓</span>}
                </button>
              ))}

              {/* Divider */}
              <div className="h-px my-1" style={{ background: "var(--border)" }} />

              {/* Auto-cycle toggle */}
              <button
                onClick={() => setAutoPlay((a) => !a)}
                className="flex items-center justify-between px-3 py-2 rounded-xl transition-all duration-200"
                style={{
                  background: autoPlay ? "var(--accent-bg)" : "transparent",
                  border: `1.5px solid ${autoPlay ? "var(--accent)" : "var(--border)"}`,
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-base">🎨</span>
                  <span className="text-sm font-medium" style={{ color: "var(--text)" }}>Auto</span>
                </div>
                {/* Toggle pill */}
                <div
                  className="relative w-9 h-5 rounded-full transition-colors duration-300 flex-shrink-0"
                  style={{ background: autoPlay ? "var(--accent)" : "var(--bg-secondary)" }}
                >
                  <motion.div
                    className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow"
                    animate={{ left: autoPlay ? "18px" : "2px" }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                </div>
              </button>
              {autoPlay && (
                <p className="text-xs text-center" style={{ color: "var(--text-muted)" }}>
                  Switching every 1 min
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Switcher;
